// src/features/recruitment/RecruitmentList.js
import React, { useState, useEffect, useMemo } from "react";
import "./Recruitment.css";
import "./RecruitmentList.css";
import config from "../../config";
import RecruitmentListFilter from "./RecruitmentListFilter";
import { CiCirclePlus } from "react-icons/ci";
import { BsSliders } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";


const RecruitmentList = ({
  activeMainTab,
  setActiveMainTab,
  onAddRecruitment,
  onViewRecruitment, // 👈 NEW: called when row is clicked
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPageList, setCurrentPageList] = useState(1);

  const itemsPerPage = 6; // we control page size in UI
  const [rows, setRows] = useState([]); // data from API (mapped to table shape)
  const [totalPages, setTotalPages] = useState(1); // from API
  const [totalRows, setTotalRows] = useState(0); // totalElements from API
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    client: "",
    status: "",
    jobRole: "",
    startDate: "",
    endDate: "",
    sortBy: "",
  });

  // userDetails for token etc.
  const [userDetails, setUserDetails] = useState({
    token: "",
    orgId: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({ token, orgId });
  }, []);

  // helper to build pagination buttons just like before
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    const firstPage = 1;
    const lastPage = totalPages;
    const prevPage = currentPageList - 1;
    const nextPage = currentPageList + 1;

    pages.push(firstPage);

    if (currentPageList > 3) {
      pages.push("dots-left");
    }

    for (let p = prevPage; p <= nextPage; p++) {
      if (p > 1 && p < lastPage) {
        pages.push(p);
      }
    }

    if (currentPageList < totalPages - 2) {
      pages.push("dots-right");
    }

    pages.push(lastPage);

    return [...new Set(pages)];
  };

  const goToPage = (p) => {
    if (p < 1) p = 1;
    if (p > totalPages) p = totalPages;
    setCurrentPageList(p);
  };

  useEffect(() => {
    // don't fetch until we have token
    if (!userDetails.token || !userDetails.orgId) return;

    const controller = new AbortController();
    const doFetch = async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const params = new URLSearchParams({
          orgKey: userDetails.orgId,
          page: currentPageList - 1,
          size: itemsPerPage,
        });

        // Search q
        if (searchTerm) params.append("q", searchTerm);

        // Filters mapping
        if (filters.status) {
          params.append("statuses", filters.status);
          params.append("statusCsv", filters.status);
        }
        if (filters.client) params.append("clientName", filters.client);
        if (filters.jobRole) params.append("jobRole", filters.jobRole);
        if (filters.startDate) params.append("startDate", filters.startDate);
        if (filters.endDate) params.append("endDate", filters.endDate);

        // Sorting mapping
        let sortValue = "id,desc"; // Default
        if (filters.sortBy === "NAME_ASC") sortValue = "jobRole,asc";
        else if (filters.sortBy === "NAME_DESC") sortValue = "jobRole,desc";
        else if (filters.sortBy === "DATE_ASC") sortValue = "startDate,asc";
        else if (filters.sortBy === "DATE_DESC") sortValue = "startDate,desc";
        params.append("sort", sortValue);

        const url = `${config.API_BASE_URL}${config.RECRUITMENT_LIST_ENDPOINT}?${params.toString()}`;
        console.log("Fetching recruitment list from:", url);

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
          signal: controller.signal,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(
            text || `Recruitment list fetch failed with status ${res.status}`
          );
        }

        const data = await res.json();
        console.log("Recruitment API Response:", data);

        const mappedRows =
          (data.content || []).map((item) => ({
            id: item.id,
            key: item.recruitmentKey || "-",
            role: item.jobRole || "-",
            client: item.clientName || "-",
            start: item.startDate || "-",
            end: item.endDate || "-",
            status: item.status || "-",
          })) || [];

        setRows(mappedRows);
        setTotalPages(data.totalPages || 1);
        setTotalRows(data.totalElements || mappedRows.length);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Recruitment list fetch error:", err);
          setErrorMsg("Unable to load recruitment list.");
          setRows([]);
          setTotalPages(1);
          setTotalRows(0);
        }
      } finally {
        setLoading(false);
      }
    };

    doFetch();

    return () => controller.abort();
  }, [userDetails.token, userDetails.orgId, currentPageList, itemsPerPage, filters, searchTerm]);

  // Server-side filtering, so displayRows just uses rows
  const displayRows = useMemo(() => {
    return rows;
  }, [rows]);

  // compute "showing X to Y of Z entries" text
  const showingFrom =
    totalRows === 0 ? 0 : (currentPageList - 1) * itemsPerPage + 1;
  const showingToCalc = currentPageList * itemsPerPage;
  const showingTo = showingToCalc > totalRows ? totalRows : showingToCalc || 0;

  return (
    <>
      {/* Top toolbar row */}
      <div className='list-top-bar'>
        {/* Search */}
        <div className='search-box-wrapper'>
  <FiSearch className='search-box-icon' />

  <input
    className='search-box-input'
    placeholder='Search'
    value={searchTerm}
    onChange={(e) => {
      setSearchTerm(e.target.value);
      setCurrentPageList(1);
    }}
  />
</div>

        {/* Toggle buttons */}
        <div className='main-tabs'>
          <button
            className={
              "main-tab-btn " +
              (activeMainTab === "pipeline" ? "active-main-tab" : "")
            }
            onClick={() => {
              setActiveMainTab("pipeline");
            }}
          >
            Recruitment Pipeline
          </button>

          <button
            className={
              "main-tab-btn " +
              (activeMainTab === "list" ? "active-main-tab" : "")
            }
            onClick={() => {
              setActiveMainTab("list");
            }}
          >
            Recruitment List
          </button>
        </div>

        {/* Right actions */}
        <div className='list-actions-right'>
          <button
            className='add-recruitment-btn'
            onClick={() => {
              if (onAddRecruitment) {
                onAddRecruitment();
              }
            }}
          >
            <span className='plus-icon'><CiCirclePlus /></span>
            Add Recruitment
          </button>

          <button
            className='filter-btn'
            onClick={() => {
              setShowFilter(true);
            }}
          >
            <span className='filter-icon'><BsSliders />
</span> Filter
          </button>
          <RecruitmentListFilter
            open={showFilter}
            onClose={() => setShowFilter(false)}
            onApply={(vals) => {
              setFilters({
                client: vals.client || "",
                status: vals.status || "",
                jobRole: vals.jobRole || "",
                startDate: vals.startDate || "",
                endDate: vals.endDate || "",
                sortBy: vals.sortBy || "",
              });
              setCurrentPageList(1);
              setShowFilter(false);
            }}
            onClear={() => {
              setFilters({
                client: "",
                status: "",
                jobRole: "",
                startDate: "",
                endDate: "",
                sortBy: "",
              });
              setCurrentPageList(1);
              setShowFilter(false);
            }}
            initialValues={filters}
          />
        </div>
      </div>

      {/* Table */}
      <div className='table-wrapper no-sticky-overflow'>
        <table className='candidate-table'>
          <thead>
            <tr>
              <th>Recruitment Key</th>
              <th>Job Role</th>
              <th>Client</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th className='status-col'>Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: "24px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : errorMsg ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: "24px", color: "red" }}
                >
                  {errorMsg}
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ textAlign: "center", padding: "24px" }}
                >
                  No records found
                </td>
              </tr>
            ) : (
              displayRows.map((item, idx) => (
                <tr
                  key={item.id ?? idx}
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    // 👇 behaves like Client -> AddClient flow
                    if (onViewRecruitment && item.id != null) {
                      onViewRecruitment(item.id);
                    } else {
                      console.warn(
                        "No onViewRecruitment handler or missing id for row:",
                        item
                      );
                    }
                  }}
                >
                  <td>{item.key}</td>
                  <td>{item.role}</td>
                  <td>{item.client}</td>
                  <td>{item.start}</td>
                  <td>{item.end}</td>
                  <td className='status-col'>
                    <span
                      className={
                        "status-pill " +
                        (item.status === "Open"
                          ? "status-open"
                          : "status-close")
                      }
                    >
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      <div className='pagination-row'>
        <div className='data-info'>
          {totalRows === 0
            ? "Showing data 0 of 0 entries"
            : `Showing data ${showingFrom} to ${showingTo} of ${totalRows} entries`}
        </div>

        <div className='pager'>
          {/* Prev */}
          <button
            className='page-btn'
            disabled={currentPageList === 1 || totalRows === 0}
            onClick={() => goToPage(currentPageList - 1)}
          >
            &lt;
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((p, i) => {
            if (p === "dots-left" || p === "dots-right") {
              return (
                <button
                  key={p + i}
                  className='page-btn'
                  disabled
                  style={{ cursor: "default" }}
                >
                  …
                </button>
              );
            }
            return (
              <button
                key={p}
                className={
                  "page-btn " + (currentPageList === p ? "active-page" : "")
                }
                onClick={() => goToPage(p)}
              >
                {p}
              </button>
            );
          })}

          {/* Next */}
          <button
            className='page-btn'
            disabled={currentPageList === totalPages || totalRows === 0}
            onClick={() => goToPage(currentPageList + 1)}
          >
            &gt;
          </button>
        </div>
      </div>
    </>
  );
};

export default RecruitmentList;
